import { z } from 'zod';

import { QUESTION_TYPE } from 'types/question.types';

import { BaseQuestionSchema } from './BaseQuestionSchema';

/**
 * Form Data Structure:
 * {
 *   label: string,
 *   type: QUESTION_TYPE.LINEAR_SCALE | NPS | SATISFACTION | CONNECTION | RETENTION,
 *   isRequired: boolean,
 *   properties: {
 *     scaleMinValue: number,
 *     scaleMaxValue: number,
 *     scaleMinLabel: string,
 *     scaleMaxLabel: string
 *   }
 * }
 */

const LinearScalePropertiesSchema = z.object({
  scaleMinValue: z.number().min(0).max(1).default(1),
  scaleMaxValue: z.number().int().min(2).max(10).default(5),
  scaleMinLabel: z.string().max(25, 'Up to 25 characters').optional(),
  scaleMaxLabel: z.string().max(25, 'Up to 25 characters').optional()
});

const LinearScaleBaseSchema = BaseQuestionSchema.extend({
  properties: LinearScalePropertiesSchema
});

export const LinearScaleSchema = LinearScaleBaseSchema.extend({
  type: z.literal(QUESTION_TYPE.LINEAR_SCALE)
});

export const NPSSchema = LinearScaleBaseSchema.extend({
  type: z.literal(QUESTION_TYPE.NPS)
});

export const SatisfactionSchema = LinearScaleBaseSchema.extend({
  type: z.literal(QUESTION_TYPE.SATISFACTION)
});

export const ConnectionSchema = LinearScaleBaseSchema.extend({
  type: z.literal(QUESTION_TYPE.CONNECTION)
});

export const RetentionSchema = LinearScaleBaseSchema.extend({
  type: z.literal(QUESTION_TYPE.RETENTION)
});

export type LinearScaleFormValues = z.infer<typeof LinearScaleSchema>;
export type NPSFormValues = z.infer<typeof NPSSchema>;
export type SatisfactionFormValues = z.infer<typeof SatisfactionSchema>;
export type ConnectionFormValues = z.infer<typeof ConnectionSchema>;
export type RetentionFormValues = z.infer<typeof RetentionSchema>;

export const LINEAR_SCALE_DEFAULTS: LinearScaleFormValues = {
  label: '',
  type: QUESTION_TYPE.LINEAR_SCALE,
  isRequired: false,
  properties: {
    scaleMinValue: 1,
    scaleMaxValue: 5,
    scaleMinLabel: '',
    scaleMaxLabel: ''
  }
};

export const NPS_DEFAULTS: NPSFormValues = {
  label: 'How likely are you to recommend this event to a colleague? ',
  type: QUESTION_TYPE.NPS,
  isRequired: false,
  properties: {
    scaleMinValue: 0,
    scaleMaxValue: 10,
    scaleMinLabel: 'Not likely at all',
    scaleMaxLabel: 'Extremely likely'
  }
};

export const SATISFACTION_DEFAULTS: SatisfactionFormValues = {
  label: 'How satisfied were you with this event?',
  type: QUESTION_TYPE.SATISFACTION,
  isRequired: false,
  properties: {
    scaleMinValue: 1,
    scaleMaxValue: 5,
    scaleMinLabel: 'Not satisfied at all',
    scaleMaxLabel: 'Extremely satisfied'
  }
};

export const CONNECTION_DEFAULTS: ConnectionFormValues = {
  label: 'How connected do you feel to other colleagues after this event?',
  type: QUESTION_TYPE.CONNECTION,
  isRequired: false,
  properties: {
    scaleMinValue: 1,
    scaleMaxValue: 5,
    scaleMinLabel: 'Not connected at all',
    scaleMaxLabel: 'Extremely connected'
  }
};

export const RETENTION_DEFAULTS: RetentionFormValues = {
  label: 'How likely are you to attend a similar event again?',
  type: QUESTION_TYPE.RETENTION,
  isRequired: false,
  properties: {
    scaleMinValue: 1,
    scaleMaxValue: 5,
    scaleMinLabel: 'Not likely at all',
    scaleMaxLabel: 'Extremely likely'
  }
};
